import { useEffect, useRef } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { FormGroup, Input, Label } from "reactstrap";
import { handleFormChange } from "../../../components/modals/EditableTable";
import { useTranslation } from "react-i18next";
import InfoTip from "../../../components/InfoTip";
import MapDisplay from "../../../components/utils/Map";

const inputClass =
  "w-full zone-settings__input bg-white border border-gray-300 rounded-lg";

const isValidObjectId = (value) =>
  typeof value === "string" &&
  value.length === 24 &&
  /^[a-f0-9]{24}$/i.test(value);

const hasValidLocation = (data) => {
  const loc = data?.location;
  return (
    Array.isArray(loc) &&
    loc.length >= 2 &&
    Number.isFinite(Number(loc[0])) &&
    Number.isFinite(Number(loc[1])) &&
    !(Number(loc[0]) === 0 && Number(loc[1]) === 0)
  );
};

const ZoneSettingsSitesCollecte = ({
  sites,
  contracts,
  formData,
  setFormData,
  showAttachment = true,
  showCollection = true,
}) => {
  const { t } = useTranslation();
  const API = process.env.REACT_APP_API_BACKEND;
  const lastFetchedAddressIdRef = useRef(null);

  const showMap = showAttachment && hasValidLocation(formData);
  const mapMarkers = showMap ? [formData] : [];

  /* Charger les coordonnées du site sélectionné (getAddressNameList ne les fournit pas). */
  useEffect(() => {
    if (!showAttachment) return undefined;

    const addressId =
      formData.addressId == null ||
      formData.addressId === "" ||
      formData.addressId === "unknown"
        ? ""
        : String(formData.addressId);

    if (!isValidObjectId(addressId)) {
      lastFetchedAddressIdRef.current = null;
      if (formData.location != null) {
        setFormData((prev) => ({ ...prev, location: null }));
      }
      return undefined;
    }

    if (hasValidLocation(formData)) {
      if (lastFetchedAddressIdRef.current == null) {
        lastFetchedAddressIdRef.current = addressId;
      }
      if (lastFetchedAddressIdRef.current === addressId) {
        return undefined;
      }
    }

    let cancelled = false;
    const token = Cookies.get("userToken");

    const fetchSiteLocation = async () => {
      try {
        const response = await axios.get(`${API}address/${addressId}`, {
          headers: { authorization: `Bearer ${token}` },
        });
        if (cancelled) return;

        const address = response?.data?.data;
        const loc = address?.location;
        lastFetchedAddressIdRef.current = addressId;

        if (hasValidLocation({ location: loc })) {
          setFormData((prev) => ({
            ...prev,
            location: loc,
          }));
        } else {
          setFormData((prev) => ({ ...prev, location: null }));
        }
      } catch {
        if (!cancelled) {
          lastFetchedAddressIdRef.current = addressId;
          setFormData((prev) => ({ ...prev, location: null }));
        }
      }
    };

    fetchSiteLocation();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch when le site change
  }, [API, showAttachment, formData.addressId, setFormData]);

  const onAttachmentSiteChange = (e) => {
    const previousId = String(formData.addressId ?? "");
    const nextId = e.target.value === "unknown" ? "" : String(e.target.value);

    handleFormChange(e, formData, setFormData);

    if (!isValidObjectId(nextId)) {
      lastFetchedAddressIdRef.current = null;
      setFormData((prev) => ({ ...prev, location: null }));
      return;
    }

    if (previousId !== nextId) {
      lastFetchedAddressIdRef.current = null;
      setFormData((prev) => ({ ...prev, location: null }));
    }
  };

  return (
    <div className="flex flex-col w-full">
      {showAttachment && (
        <FormGroup className="mb-2">
          <Label className="zone-settings__label">
            {t("containerSettings.attachmentSite")}{" "}
            <span className="zone-settings__required">*</span>
          </Label>
          <Input
            className={inputClass}
            type="select"
            value={
              formData.addressId == null || formData.addressId === ""
                ? "unknown"
                : String(formData.addressId)
            }
            onChange={onAttachmentSiteChange}
            name="addressId"
            secondfield="addressName"
          >
            <option value="unknown">
              {t("containerSettings.unspecified")}
            </option>
            {sites.map((site) => (
              <option
                key={site._id}
                value={String(site._id)}
                secondvalue={site.name}
              >
                {site.name}
              </option>
            ))}
          </Input>
        </FormGroup>
      )}

      {showAttachment && (
        <div className="zone-settings__image-frame mb-4 overflow-hidden bg-gray-50">
          {showMap ? (
            <MapDisplay
              fillingRateDelivery={[]}
              markers={mapMarkers}
              mapHeight="334px"
              isActivity={true}
            />
          ) : (
            <div
              className="w-full flex items-center justify-center text-gray-500 text-sm"
              style={{ minHeight: "220px" }}
            >
              {t("containerSettings.mapAttachmentPoint")}
            </div>
          )}
        </div>
      )}

      {showAttachment && (
        <FormGroup className="mb-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Label className="zone-settings__label mb-0">
              {t("containerSettings.attachmentPointDescription")}
            </Label>
            <InfoTip nudgeY={1}>
              {t(
                "Additional description for the address to identify the affiliation point",
              )}
              . {t("containerSettings.attachmentPointDescriptionPlaceholder")}
            </InfoTip>
          </div>
          <Input
            className={inputClass}
            type="text"
            placeholder={t(
              "containerSettings.attachmentPointDescriptionPlaceholder",
            )}
            value={formData.positioning ?? ""}
            onChange={(e) => handleFormChange(e, formData, setFormData)}
            name="positioning"
          />
        </FormGroup>
      )}

      {showCollection && (
        <FormGroup className="mb-4">
          <Label className="zone-settings__label">
            {t("containerSettings.destinationSite")}
          </Label>
          <Input
            className={inputClass}
            type="select"
            value={
              formData.addressDestinationId == null ||
              formData.addressDestinationId === ""
                ? "unknown"
                : String(formData.addressDestinationId)
            }
            onChange={(e) => handleFormChange(e, formData, setFormData)}
            name="addressDestinationId"
          >
            <option value="unknown">
              {t("containerSettings.unspecified")}
            </option>
            {sites.map((site) => (
              <option key={site._id} value={String(site._id)}>
                {site.name}
              </option>
            ))}
          </Input>
        </FormGroup>
      )}

      {showCollection && (
        <FormGroup className="mb-0">
          <Label className="zone-settings__label">
            {t("containerSettings.collectionPlanningEmail")}
          </Label>
          <Input
            className={inputClass}
            type="select"
            value={
              formData.contract_id == null || formData.contract_id === ""
                ? "unknown"
                : String(formData.contract_id)
            }
            onChange={(e) => handleFormChange(e, formData, setFormData)}
            name="contract_id"
            secondfield="collectorEmail"
          >
            <option value="unknown">{t("Contract not provided")}</option>
            {(contracts || []).map((contract) => (
              <option
                key={contract._id}
                value={String(contract._id)}
                secondvalue={contract.collectorEmail || ""}
              >
                {`${contract.contract_number || "—"}${contract?.name ? ` (${contract.name})` : ""} - ${contract.collectorEmail || "—"}`}
              </option>
            ))}
          </Input>
        </FormGroup>
      )}
    </div>
  );
};

export default ZoneSettingsSitesCollecte;
